import { v4 as uuidv4 } from 'uuid';

export const ONE_TAP_ATTEMPT_PARAM = 'one_tap_attempt_id';
export const ONE_TAP_PENDING_STORAGE_KEY = 'bx-one-tap-pending';
export const ONE_TAP_REPORTED_STORAGE_PREFIX = 'bx-one-tap-reported:';
export const ONE_TAP_MAX_AGE_MS = 24 * 60 * 60 * 1000;
// authenticated_at comes from G2's clock, which this browser's clock may lag.
const SERVER_CLOCK_SKEW_MS = 5 * 60 * 1000;

export type OneTapAuthOutcome = {
  attemptId: string;
  method: 'google_one_tap';
  isSignUp: boolean;
  authenticatedAt: number;
};

type OneTapPendingAttempt = {
  attemptId: string;
  submittedAt: number;
};

type StorageLike = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

const browserStorage = (): StorageLike | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const isFresh = (timestamp: number, now: number, skewMs = 0): boolean =>
  Number.isFinite(timestamp) &&
  timestamp <= now + skewMs &&
  now - timestamp <= ONE_TAP_MAX_AGE_MS;

export const createOneTapAttemptId = (): string => uuidv4();

export const appendOneTapAttemptId = (
  authorizeUrl: string,
  attemptId: string
): string => {
  const url = new URL(authorizeUrl);
  url.searchParams.set(ONE_TAP_ATTEMPT_PARAM, attemptId);
  return url.toString();
};

export const buildOneTapMarketingEvent = (
  formStepName: string,
  formSubmissionId: string,
  formSubmittedResult?: 'log-in' | 'sign-up'
) => ({
  name: 'email_capture',
  form_type: 'uw',
  form_name: 'one-tap',
  form_step_name: formStepName,
  form_submission_id: formSubmissionId,
  ...(formSubmittedResult
    ? { form_submitted_result: formSubmittedResult }
    : {}),
});

export const storeOneTapPendingAttempt = (
  attemptId: string,
  now = Date.now(),
  storage: StorageLike | null = browserStorage()
): boolean => {
  if (!storage || !attemptId) {
    return false;
  }

  try {
    storage.setItem(
      ONE_TAP_PENDING_STORAGE_KEY,
      JSON.stringify({
        attemptId,
        submittedAt: now,
      } satisfies OneTapPendingAttempt)
    );
    return true;
  } catch {
    return false;
  }
};

const parsePendingAttempt = (
  value: string | null
): OneTapPendingAttempt | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as Partial<OneTapPendingAttempt>;
    if (
      typeof parsed.attemptId !== 'string' ||
      !parsed.attemptId ||
      typeof parsed.submittedAt !== 'number'
    ) {
      return null;
    }

    return parsed as OneTapPendingAttempt;
  } catch {
    return null;
  }
};

const removePendingAttempt = (storage: StorageLike): void => {
  try {
    storage.removeItem(ONE_TAP_PENDING_STORAGE_KEY);
  } catch {
    // Storage is best effort and must never block auth.
  }
};

export const clearOneTapPendingAttempt = (
  storage: StorageLike | null = browserStorage()
): void => {
  if (storage) {
    removePendingAttempt(storage);
  }
};

export const isOneTapAuthOutcome = (
  value: unknown
): value is OneTapAuthOutcome => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const outcome = value as Partial<OneTapAuthOutcome>;
  return (
    typeof outcome.attemptId === 'string' &&
    outcome.attemptId.length > 0 &&
    outcome.method === 'google_one_tap' &&
    typeof outcome.isSignUp === 'boolean' &&
    typeof outcome.authenticatedAt === 'number'
  );
};

/**
 * Call after a G2 session has authenticated. The browser marker records a
 * returned Google credential; optional server metadata must match that attempt
 * when supplied, but its absence must not suppress a successful authentication.
 * Starting a new unauthenticated prompt clears abandoned attempts.
 */
export const consumeMatchingOneTapAttempt = (
  outcome: unknown,
  now = Date.now(),
  storage: StorageLike | null = browserStorage()
): string | null => {
  if (!storage) {
    return null;
  }

  let pendingValue: string | null = null;
  try {
    pendingValue = storage.getItem(ONE_TAP_PENDING_STORAGE_KEY);
  } catch {
    return null;
  }

  const pending = parsePendingAttempt(pendingValue);
  if (!pending) {
    removePendingAttempt(storage);
    return null;
  }

  if (!isFresh(pending.submittedAt, now)) {
    removePendingAttempt(storage);
    return null;
  }

  if (
    outcome != null &&
    (!isOneTapAuthOutcome(outcome) ||
      !isFresh(outcome.authenticatedAt * 1000, now, SERVER_CLOCK_SKEW_MS) ||
      pending.attemptId !== outcome.attemptId)
  ) {
    removePendingAttempt(storage);
    return null;
  }

  const reportedKey = `${ONE_TAP_REPORTED_STORAGE_PREFIX}${pending.attemptId}`;
  try {
    if (storage.getItem(reportedKey)) {
      removePendingAttempt(storage);
      return null;
    }
    storage.setItem(reportedKey, '1');
  } catch {
    // Auth must continue when browser storage is unavailable. The in-memory
    // caller-level guard still prevents duplicate effects in this document.
  }

  removePendingAttempt(storage);
  return pending.attemptId;
};
